'use client'

import * as React from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import algoliaAnalytics from 'search-insights'
import { useInstantSearch, Configure } from 'react-instantsearch'
import { InstantSearchNext } from 'react-instantsearch-nextjs'
import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares'
import { defaultSearchClient, algoliaProductIndex } from 'algolia/algolia'
import type { UiState } from 'instantsearch.js'

import { getSearchState, searchStateToQuery } from './utils'

function InsightsMiddleware() {
  const { addMiddlewares } = useInstantSearch()

  React.useEffect(() => {
    const middleware = createInsightsMiddleware({
      insightsClient: algoliaAnalytics,
    })

    return addMiddlewares(middleware)
  }, [addMiddlewares])

  return null
}

export const SearchPageProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter()
  const query = useSearchParams()
  const pathname = usePathname()

  const routerQuery = query.toString()

  const searchState = getSearchState({
    url: routerQuery,
  })

  const initialUiState = React.useMemo(() => {
    const indexUiState = {
      ...searchState.uiState?.[algoliaProductIndex],
    }

    if (typeof indexUiState.page === 'number' && indexUiState.page < 1) {
      indexUiState.page = 1
    }

    return {
      [algoliaProductIndex]: indexUiState,
    }
  }, [searchState.uiState])

  const handleStateChange = React.useCallback(
    ({
      uiState,
      setUiState,
    }: {
      uiState: UiState
      setUiState: (uiState: UiState | ((previousUiState: UiState) => UiState)) => void
    }) => {
      const newUiState = { ...uiState }
      const indexState = newUiState[algoliaProductIndex]

      if (typeof indexState?.page === 'number' && indexState.page < 1) {
        indexState.page = 1
      }

      const newQuery = searchStateToQuery({ uiState: newUiState })
      const currentQuery = query.toString()

      // oxlint-disable-next-line no-negated-condition
      if (newQuery !== currentQuery) {
        setUiState(newUiState)
        router.replace(`${pathname}?${newQuery}`, { scroll: false })
      } else {
        setUiState(newUiState)
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [query, pathname],
  )

  return (
    <InstantSearchNext
      initialUiState={initialUiState}
      onStateChange={handleStateChange}
      searchClient={defaultSearchClient}
      indexName={algoliaProductIndex}
    >
      <Configure hitsPerPage={12} />
      {children}
      <InsightsMiddleware />
    </InstantSearchNext>
  )
}
